import { useState, type ReactNode, type SyntheticEvent } from 'react';
import { Alert } from '../ui/Alert.js';
import { Button } from '../ui/Button.js';
import { Dialog } from '../ui/Dialog.js';
import { TextField } from '../ui/Field.js';

/**
 * The confirm dialog section 12 puts in front of medium and high risk actions.
 *
 * `confirmPhrase` is the stronger form: the person types the thing's own name
 * before the button works. Section 12 asks for it by name on blocking a verified
 * search engine, and it belongs anywhere the action cannot be undone by clicking
 * again — removing a server, for one.
 */

export interface ConfirmDialogProps {
  title: string;
  description: string;
  confirmLabel: string;
  /** When set, the button stays disabled until this is typed exactly. */
  confirmPhrase?: string;
  danger?: boolean;
  busy?: boolean;
  error?: string | null;
  onConfirm: () => void;
  onCancel: () => void;
  children?: ReactNode;
}

export function ConfirmDialog({
  title,
  description,
  confirmLabel,
  confirmPhrase,
  danger = false,
  busy = false,
  error = null,
  onConfirm,
  onCancel,
  children,
}: ConfirmDialogProps): ReactNode {
  const [typed, setTyped] = useState('');
  const unlocked = confirmPhrase === undefined || typed === confirmPhrase;

  const submit = (event: SyntheticEvent): void => {
    event.preventDefault();
    if (unlocked) onConfirm();
  };

  return (
    <Dialog title={title} description={description} onClose={onCancel}>
      <form onSubmit={submit} className="stack">
        {children}
        {confirmPhrase !== undefined && (
          <TextField
            label={`Type ${confirmPhrase} to confirm`}
            autoFocus
            autoComplete="off"
            spellCheck={false}
            value={typed}
            onChange={(event) => {
              setTyped(event.target.value);
            }}
          />
        )}
        {error !== null && <Alert tone="danger">{error}</Alert>}
        <div className="dialog-actions">
          <Button onClick={onCancel}>Cancel</Button>
          <Button
            type="submit"
            variant={danger ? 'danger' : 'primary'}
            busy={busy}
            busyLabel="Working…"
            disabled={!unlocked}
          >
            {confirmLabel}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
